import { shortenAddress } from '@/utils/address/shortenAddress'
import { cn } from '@/utils/cn'
import { copyToClipboard } from '@/utils/copyToClipboard'
import { getChainNameById } from '@/utils/network/getChainNameById'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { useState } from 'react'
import type { Address, Chain } from 'viem'
import { Button } from '../common/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogContentWrapper,
  DialogScrollArea,
  DialogTrigger,
} from '../common/dialog'
import { Label } from '../common/label'
import { NetworkImage } from '../common/networkImage'
import { QRCode } from '../common/qrCode'
import { Separator } from '../common/separator'
import { CopyIcon } from '../icons/copy'
import { NetworkSelect } from '../shared/networkSelect'

interface FundWalletProps {
  address: Address
  chain: Chain
  className?: string
}

export const FundWalletDialog = ({ className, ...props }: FundWalletProps) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="input" size="sm" className={cn('bg-page', className)}>
          Fund your wallet
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogBody>
          <DialogScrollArea>
            <DialogContentWrapper className="pt-10 md:pt-16">
              <FundWalletContent {...props} />
            </DialogContentWrapper>
          </DialogScrollArea>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

const FundWalletContent = ({ address, chain: initialChain }: FundWalletProps) => {
  const [chain, setChain] = useState(initialChain)

  return (
    <div className="flex flex-col gap-4 lg:gap-5">
      {chain && (
        <>
          <Label className="flex flex-col items-stretch gap-1">
            Network
            <NetworkSelect chain={chain} modal={true} onSelect={setChain} />
          </Label>
          <Separator />
        </>
      )}
      <WalletInfo address={address} chain={chain} />
    </div>
  )
}

const WalletInfo = ({ address, chain }: FundWalletProps) => {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border bg-page p-4">
      <div className="flex flex-col gap-2">
        <p className="flex flex-wrap items-center gap-2">
          Supply tokens on
          <span className="flex items-center gap-1">
            <NetworkImage network={getChainNameById(chain.id)} />
            <span className="capitalize">{getNetworkName(getChainNameById(chain.id))}</span>
          </span>
        </p>
        <p>Copy address or use QR below to send funds to your wallet</p>
        <div className="flex items-center gap-2">
          <p className="text-heading-foreground">{shortenAddress(address)}</p>
          <Button
            variant="input"
            size="icon"
            onClick={() => copyToClipboard(address)}
            className="size-9 min-w-9 rounded-full bg-page"
          >
            <CopyIcon size={20} />
          </Button>
        </div>
      </div>
      <Separator />
      <div className="mx-auto">
        <QRCode data={address} width={220} height={220} />
      </div>
    </div>
  )
}
